import { gql, useQuery } from '@apollo/client';
import { Box, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { StatusBadge } from '../components/coreComponents/badge';
import { IconButton } from '../components/coreComponents/iconButton';
import { Table } from '../components/microLayouts/table';
import { AccountProvisionDrawer } from './accountProvisionDrawer';

interface Account {
  readonly id: string;
  readonly alias?: string | null;
  readonly createdAt: string;
  readonly inviteLimit?: number | null;
  readonly inviteUseLimit?: number | null;
  readonly publishedAt?: string | null;
}

export function Dashboard() {
  const { data, loading, error, refetch } = useQuery(query);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  if (error) return <Typography color="error">Error: {error.message}</Typography>;

  const accounts: Account[] = data?.accountMany.items ?? [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5">Accounts</Typography>
        <IconButton
          variant="accentFilled"
          size="md"
          icon="add"
          onClick={() => setDrawerOpen(true)}
        />
      </Stack>

      <AccountProvisionDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          // createNewAccount runs asynchronously in the seed worker, so the
          // first refetch fired at mutation submit (onProvisioned) hits the
          // global DB before the Account row exists. Refetch again on close
          // — by the time the user dismisses the drawer the row is there.
          refetch();
        }}
        onProvisioned={() => refetch()}
      />

      <Table
        items={loading ? undefined : accounts}
        rowKey={item => item.id}
        rowProps={item => ({
          onClick: () => navigate(`/accounts/${item.id}/overview`),
          sx: { cursor: 'pointer' }
        })}
        columns={[
          {
            label: 'ID',
            render: item => (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {item.id}
              </Typography>
            )
          },
          {
            label: 'Alias',
            render: item => item.alias ?? '-'
          },
          {
            label: 'Created',
            render: item => new Date(item.createdAt).toLocaleDateString()
          },
          {
            label: 'Invite Limit',
            render: item => item.inviteLimit ?? '-'
          },
          {
            label: 'Invite Use Limit',
            render: item => item.inviteUseLimit ?? '-'
          },
          {
            label: 'Status',
            render: item =>
              item.publishedAt != null ? (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <StatusBadge tone="error" label="PUBLISHED" isUppercase />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {new Date(item.publishedAt).toLocaleDateString()}
                  </Typography>
                </Stack>
              ) : (
                <StatusBadge tone="default" label="DRAFT" isUppercase />
              )
          }
        ]}
      />
    </Box>
  );
}

const query = gql<AccountManyGQL>`
  query AccountMany {
    accountMany {
      items {
        id
        alias
        createdAt
        inviteLimit
        inviteUseLimit
        publishedAt
      }
    }
  }
`;
